"use client";

import { useState } from "react";
import type { SectionState } from "../navigation";
import { OrganizationProgressDonut } from "./OrganizationProgressDonut";
import { OrganizationSectionStatusIcon } from "./OrganizationSectionStatusIcon";
import type {
  OrganizationSectionCardData,
  OrganizationSectionMeta,
} from "./organization-sections";
import { OrganizationSectionIcon } from "./organization-sections";

export interface OrganizationBreadcrumbProps<
  SectionId extends string = string,
> {
  activeSection: SectionId;
  completeSections: number;
  sections: OrganizationSectionMeta<SectionId>[];
  sectionCards: OrganizationSectionCardData<SectionId>[];
  onNavigate: (sectionId: SectionId | null) => void;
  rootLabel?: string;
}

type BreadcrumbMenuItem<SectionId extends string = string> = {
  id: SectionId;
  label: string;
  description?: string;
  icon: React.ReactNode;
  state: SectionState;
  statusText: string;
};

export function OrganizationBreadcrumb<SectionId extends string = string>({
  activeSection,
  completeSections,
  sections,
  sectionCards,
  onNavigate,
  rootLabel = "Organization",
}: OrganizationBreadcrumbProps<SectionId>) {
  const [isOpen, setIsOpen] = useState(false);
  const activeMeta = sections.find((section) => section.id === activeSection);
  const menuItems: BreadcrumbMenuItem<SectionId>[] = sections.map((section) => {
    const card = sectionCards.find(
      (cardSection) => cardSection.id === section.id,
    );

    return {
      id: section.id,
      label: section.detailLabel ?? section.title,
      description: section.blurb,
      icon: section.icon,
      state: card?.state ?? "gray",
      statusText: card?.statusText ?? "Not started",
    };
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className="sticky top-0 z-20 -mx-6 mb-6 border-b border-(--border-subtle) bg-(--bg-white) px-6 py-3"
    >
      <div className="relative flex items-center gap-5 text-[15px]">
        <button
          type="button"
          onClick={() => onNavigate(null)}
          className="text-(--fg-secondary) hover:text-(--fg-primary) transition-colors focus-visible:outline-2 focus-visible:outline-(--fg-brand) focus-visible:outline-offset-2 rounded"
        >
          {rootLabel}
        </button>
        <span className="text-(--fg-muted)" aria-hidden>
          /
        </span>
        <button
          type="button"
          onClick={() => setIsOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className={[
            "box-border inline-flex h-[38px] items-center gap-2.5 rounded-[3px] border bg-(--bg-white) py-0 pr-3.5 pl-2 text-left text-[13px] font-medium text-(--fg-primary)",
            "transition-[border-color,background] duration-[120ms] hover:border-(--border-default)",
            "focus-visible:outline-2 focus-visible:outline-(--fg-brand) focus-visible:outline-offset-2",
            isOpen ? "border-(--fg-brand)" : "border-(--border-subtle)",
          ].join(" ")}
        >
          <span className="flex items-center gap-2.5 min-w-0">
            <OrganizationProgressDonut
              complete={completeSections}
              total={sections.length}
            />
            <span className="truncate font-medium">
              {activeMeta?.detailLabel ?? activeMeta?.title ?? ""}
            </span>
          </span>
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={
              isOpen
                ? "rotate-180 text-(--fg-primary) transition-transform duration-[120ms]"
                : "text-(--fg-muted) transition-transform duration-[120ms]"
            }
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute left-[143px] top-[calc(100%+6px)] z-20 flex min-w-[360px] flex-col gap-px rounded border border-(--border-default) bg-(--bg-white) p-1 shadow-md">
            <div className="mb-1 flex items-center gap-2.5 border-b border-(--border-subtle) px-3 py-2.5">
              <OrganizationProgressDonut
                complete={completeSections}
                total={sections.length}
                size={22}
              />
              <div className="inline-flex items-baseline gap-1.5 text-(--fg-primary)">
                <span className="font-mono text-[13px] font-semibold tracking-[-0.02em] tabular-nums">
                  {completeSections}/{sections.length}
                </span>
                <span className="text-xs text-(--fg-secondary)">
                  Sections Complete
                </span>
              </div>
            </div>
            <div role="menu" className="flex flex-col gap-px">
              {menuItems.map((item) => {
                const isActive = item.id === activeSection;
                const isComplete = item.state === "complete";

                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onNavigate(item.id);
                      setIsOpen(false);
                    }}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-[3px] px-2.5 py-2 text-left text-[13px] text-(--fg-primary) transition-colors duration-100 hover:bg-(--bg-hover,var(--bg-canvas))",
                      isActive ? "bg-(--bg-canvas)" : "",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px]",
                        isComplete
                          ? "bg-(--brand-navy) text-(--fg-inverse)"
                          : "border border-(--border-subtle) bg-(--bg-focused,var(--bg-canvas)) text-(--fg-muted)",
                      ].join(" ")}
                      aria-hidden
                    >
                      <OrganizationSectionIcon
                        icon={item.icon}
                        size={14}
                        strokeWidth={1.9}
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate font-medium text-(--fg-primary)">
                        {item.label}
                      </span>
                    </span>
                    <OrganizationSectionStatusIcon state={item.state} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
